import querystring from 'querystring';
import _ from 'lodash';
import Config from '../../AppConfig';
import {
	PlanProfile,
	PlanDetails,
	PlanInvestments,
	PlanMemberDemographics,
	MemberBenefits,
	PlanTransactions,
} from '../domains/planProfile.domain';
import { get } from './core/fetch';

const _get = async (
	api: string,
	key: string,
	pageNumber?: number,
	pageSize?: number,
	search?: string
) => {
	try {
		return await get(composeUrl(key, api, { pageNumber, pageSize, search }), {
			withAuth: true,
			noGateway: false,
		});
	} catch (e) {
		return undefined;
	}
};

export const getPlanProfile = async (
	planUUID: string
): Promise<PlanProfile | undefined> => {
	return _get('profile', planUUID) as Promise<PlanProfile>;
};

export const getPlanDetails = async (
	planUUID: string
): Promise<PlanDetails | undefined> => {
	return _get('details', planUUID) as Promise<PlanDetails>;
};

export const getMemberBenefits = async (
	planUUID: string,
	pageNumber?: number,
	pageSize?: number,
	search?: string
): Promise<MemberBenefits | undefined> => {
	return _get('benefits', planUUID, pageNumber, pageSize, search) as Promise<
		MemberBenefits
	>;
};

export const getPlanInvestments = async (
	planUUID: string
): Promise<PlanInvestments | undefined> => {
	return _get('investments', planUUID) as Promise<PlanInvestments>;
};

export const getPlanTransactions = async (
	planUUID: string,
	pageNumber?: number,
	pageSize?: number,
	search?: string
): Promise<PlanTransactions | undefined> => {
	return _get(
		'transactions',
		planUUID,
		pageNumber,
		pageSize,
		search
	) as Promise<PlanTransactions>;
};

export const getPlanMemberDemographics = async (
	planUUID: string
): Promise<PlanMemberDemographics | undefined> => {
	return _get('demographics', planUUID) as Promise<PlanMemberDemographics>;
};

export const fetchMembersInBatches = async (
	planUUID: string,
	totalCount: number,
	pageSize?: number
) => {
	return fetchInBatch(
		getMemberBenefits,
		'members',
		planUUID,
		totalCount,
		pageSize
	);
};

export const fetchTransactionsInBatches = async (
	planUUID: string,
	totalCount: number,
	pageSize?: number
) => {
	return fetchInBatch(
		getPlanTransactions,
		'transactions',
		planUUID,
		totalCount,
		pageSize
	);
};

const fetchInBatch = async (
	fetchData: (planUUID: string, pageNumber: number, pageSize: number) => any,
	responseField: string,
	planUUID: string,
	totalCount: number,
	pageSize?: number
) => {
	pageSize = pageSize ? pageSize : MAX_PAGE_SIZE;
	const totalRequestsNeeded = Math.ceil(totalCount / pageSize);

	const allResults = [];
	for (let i = 0; i < totalRequestsNeeded; i++) {
		allResults.push(await fetchData(planUUID, i + 1, pageSize));
	}
	return _.flatten(_.map(allResults, responseField));
};

const MAX_PAGE_SIZE = 5000;

const composeUrl = (key: string, api: string, queryParams: any) => {
	const url = `${Config.mlcApiPrefix}/plans/${key}/${api}`;
	for (key in queryParams) {
		if (!queryParams[key]) {
			delete queryParams[key];
		}
	}
	return _.isEmpty(queryParams)
		? url
		: url.concat(`?${querystring.stringify(queryParams)}`);
};
